import { useState } from 'react';
import { Gamepad2, Grid3X3, ArrowUpRight, ChevronLeft, Trophy, Zap, Target } from 'lucide-react';
import SnakeGame from '../games/SnakeGame';
import Game2048 from '../games/Game2048';

interface Game {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  difficulty: string;
  component: React.ReactNode;
}

export default function GameCenter() {
  const [activeGame, setActiveGame] = useState<string | null>(null);

  const games: Game[] = [
    {
      id: 'snake',
      name: '贪吃蛇',
      description: '经典贪吃蛇游戏，吃掉食物变长，不要撞墙！',
      icon: <Zap className="w-6 h-6" />,
      color: 'bg-green-500',
      difficulty: '简单',
      component: <SnakeGame />
    },
    {
      id: '2048',
      name: '2048',
      description: '滑动方块，合并相同数字，达到2048！',
      icon: <Grid3X3 className="w-6 h-6" />,
      color: 'bg-orange-500',
      difficulty: '中等',
      component: <Game2048 />
    },
    {
      id: 'tetris',
      name: '俄罗斯方块',
      description: '经典方块消除游戏（即将上线）',
      icon: <Target className="w-6 h-6" />,
      color: 'bg-blue-500',
      difficulty: '中等',
      component: null
    },
    {
      id: 'breakout',
      name: '打砖块',
      description: '用挡板击球，打碎所有砖块（即将上线）',
      icon: <ArrowUpRight className="w-6 h-6" />,
      color: 'bg-red-500',
      difficulty: '简单',
      component: null
    }
  ];

  if (activeGame) {
    const game = games.find(g => g.id === activeGame);
    if (game?.component) {
      return (
        <div className="animate-fade-in">
          <div className="sticky top-0 z-40 bg-telegram-dark/95 backdrop-blur-md border-b border-telegram-secondary/10 px-4 py-3">
            <div className="flex items-center">
              <button
                onClick={() => setActiveGame(null)}
                className="p-2 -ml-2 mr-3 rounded-full hover:bg-telegram-secondary/20 transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-telegram-text" />
              </button>
              <h1 className="text-lg font-semibold text-telegram-text">{game.name}</h1>
            </div>
          </div>
          {game.component}
        </div>
      );
    }
  }

  return (
    <div className="animate-fade-in px-4 pt-4">
      {/* 标题 */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-telegram-text flex items-center">
          <Gamepad2 className="w-7 h-7 mr-2 text-purple-400" />
          游戏中心
        </h2>
        <p className="text-sm text-telegram-secondary mt-1">
          畅玩经典休闲小游戏
        </p>
      </div>

      {/* 游戏列表 */}
      <div className="grid grid-cols-2 gap-3">
        {games.map((game) => (
          <button
            key={game.id}
            onClick={() => game.component ? setActiveGame(game.id) : null}
            disabled={!game.component}
            className={`relative p-4 rounded-xl transition-all ${
              game.component 
                ? 'bg-telegram-dark hover:bg-telegram-dark/80 active:scale-95' 
                : 'bg-telegram-dark/50 opacity-60'
            }`}
          >
            <div className={`w-12 h-12 ${game.color} rounded-xl flex items-center justify-center mb-3`}>
              {game.icon}
            </div>
            <h3 className="font-semibold text-telegram-text text-sm">{game.name}</h3>
            <p className="text-xs text-telegram-secondary mt-1 line-clamp-2">{game.description}</p>
            <div className="flex items-center mt-2">
              <span className={`text-xs px-2 py-0.5 rounded ${
                game.difficulty === '简单' ? 'bg-green-500/20 text-green-400' :
                game.difficulty === '中等' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-red-500/20 text-red-400'
              }`}>
                {game.difficulty}
              </span>
              {!game.component && (
                <span className="text-xs text-telegram-secondary/60 ml-2">即将上线</span>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* 排行榜 */}
      <div className="mt-6 p-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl border border-purple-500/30">
        <div className="flex items-center space-x-2 mb-3">
          <Trophy className="w-5 h-5 text-yellow-400" />
          <h3 className="font-semibold text-telegram-text">排行榜</h3>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2 bg-telegram-dark rounded-lg">
            <span className="text-sm text-telegram-text">贪吃蛇</span>
            <span className="text-sm text-yellow-400 font-bold">
              {localStorage.getItem('snakeHighScore') || '0'} 分
            </span>
          </div>
          <div className="flex items-center justify-between p-2 bg-telegram-dark rounded-lg">
            <span className="text-sm text-telegram-text">2048</span>
            <span className="text-sm text-orange-400 font-bold">
              {localStorage.getItem('2048HighScore') || '0'} 分
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
